import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from './SEOHead';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services';
import { Building2, Lock, User, ArrowRight, ArrowLeft, Eye, EyeOff, Shield, Loader2, CheckCircle, Mail, KeyRound, X, RefreshCw } from 'lucide-react';

const Login = () => {
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [clientData, setClientData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    clientCode: ''
  });
  const [formError, setFormError] = useState('');

  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fpStep, setFpStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState(['', '', '', '', '', '']);
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpShowPassword, setFpShowPassword] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');
  const [fpSuccess, setFpSuccess] = useState('');
  const [fpMaskedEmail, setFpMaskedEmail] = useState('');
  const [fpResendTimer, setFpResendTimer] = useState(0);
  const otpRefs = useRef([]);

  // Resend timer countdown
  useEffect(() => {
    if (fpResendTimer > 0) {
      const timer = setTimeout(() => setFpResendTimer(fpResendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [fpResendTimer]);

  // Auto-verify on client code change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.clientCode.length >= 3 && step === 1) {
        autoVerifyClient();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.clientCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
    if (error) clearError();
  };

  const autoVerifyClient = async () => {
    if (!formData.clientCode.trim() || formData.clientCode.length < 3) return;
    
    setVerifying(true);
    try {
      const response = await authAPI.verifyClient(formData.clientCode.trim());
      if (response.status === 'success' && response.data.client) {
        setClientData(response.data.client);
      } else {
        setClientData(null);
      }
    } catch (err) {
      setClientData(null);
    } finally {
      setVerifying(false);
    }
  };

  const verifyClientCode = async (e) => {
    e.preventDefault();
    
    if (!formData.clientCode.trim()) {
      setFormError('Please enter your organization code');
      return;
    }

    if (clientData) {
      setStep(2);
      return;
    }

    setVerifying(true);
    setFormError('');
    if (error) clearError();

    try {
      const response = await authAPI.verifyClient(formData.clientCode.trim());
      
      if (response.status === 'success' && response.data.client) {
        setClientData(response.data.client);
        setStep(2);
      } else {
        setFormError('Invalid organization code');
      }
    } catch (err) {
      setFormError(err.message || 'Organization not found. Please check the code.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setFormError('Please enter username and password');
      return;
    }

    const loginData = {
      username: formData.username,
      password: formData.password,
      clientCode: clientData?.isSuperAdmin ? '' : formData.clientCode
    };

    const result = await login(loginData);
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const resetForm = () => {
    setStep(1);
    setClientData(null);
    setFormData({ username: '', password: '', clientCode: '' });
    setFormError('');
    if (error) clearError();
  };

  // Forgot Password Handlers
  const openForgotPassword = () => {
    setShowForgotPassword(true);
    setFpStep(1);
    setFpEmail('');
    setFpOtp(['', '', '', '', '', '']);
    setFpNewPassword('');
    setFpConfirmPassword('');
    setFpError('');
    setFpSuccess('');
    setFpMaskedEmail('');
    setFpResendTimer(0);
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setFpStep(1);
    setFpError('');
    setFpSuccess('');
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!fpEmail.trim()) {
      setFpError('Please enter your registered email');
      return;
    }
    setFpLoading(true);
    setFpError('');
    try {
      const response = await authAPI.forgotPassword(fpEmail.trim(), formData.clientCode);
      if (response?.status === 'success') {
        setFpMaskedEmail(response?.data?.email || fpEmail);
        setFpStep(2);
        setFpResendTimer(60);
        setFpSuccess('OTP sent to your email');
      } else {
        setFpError(response?.message || 'Failed to send OTP');
      }
    } catch (err) {
      setFpError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...fpOtp];
    newOtp[index] = value;
    setFpOtp(newOtp);
    setFpError('');
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !fpOtp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setFpOtp(newOtp);
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = fpOtp.join('');
    if (otpString.length !== 6) {
      setFpError('Please enter the complete 6-digit OTP');
      return;
    }
    setFpLoading(true);
    setFpError('');
    try {
      const response = await authAPI.verifyOtp(fpEmail.trim(), otpString, formData.clientCode);
      if (response?.status === 'success') {
        setFpStep(3);
        setFpSuccess('');
      } else {
        setFpError(response?.message || 'Invalid OTP');
      }
    } catch (err) {
      setFpError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!fpNewPassword || !fpConfirmPassword) {
      setFpError('Please fill in both password fields');
      return;
    }
    if (fpNewPassword.length < 6) {
      setFpError('Password must be at least 6 characters');
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      setFpError('Passwords do not match');
      return;
    }
    setFpLoading(true);
    setFpError('');
    try {
      const otpString = fpOtp.join('');
      const response = await authAPI.resetPassword(fpEmail.trim(), otpString, fpNewPassword, formData.clientCode);
      if (response?.status === 'success') {
        setFpSuccess('Password reset successfully! You can now login.');
        setTimeout(() => {
          closeForgotPassword();
        }, 2000);
      } else {
        setFpError(response?.message || 'Failed to reset password');
      }
    } catch (err) {
      setFpError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (fpResendTimer > 0) return;
    setFpLoading(true);
    setFpError('');
    try {
      const response = await authAPI.forgotPassword(fpEmail.trim(), formData.clientCode);
      if (response?.status === 'success') {
        setFpResendTimer(60);
        setFpOtp(['', '', '', '', '', '']);
        setFpSuccess('New OTP sent to your email');
      } else {
        setFpError(response?.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setFpError(err.message || 'Failed to resend OTP');
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 flex">
      <SEOHead
        title="Login — DIGIHR HR Management Platform"
        description="Sign in to DIGIHR to manage your organisation's HR — attendance, leave, payroll, expenses, performance, and more."
        keywords="DIGIHR login, HR software login India"
        canonical="/login"
        noindex
      />
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-blue-900 text-white flex-col justify-between p-12">
        <div>
          <div className="flex items-center">
            <img 
              src="/logo.png" 
              alt="DIGIHR" 
              className="h-16 object-contain brightness-0 invert"
            />
          </div>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-bold leading-tight">
            Streamline Your<br />
            HR Operations
          </h1>
          <p className="text-blue-200 text-lg max-w-md">
            Manage attendance, payroll, and employee data all in one place. 
            Simple, secure, and efficient.
          </p>
          
          <div className="space-y-4 pt-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-800 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="text-blue-100">Real-time attendance tracking</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-800 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="text-blue-100">Automated payroll processing</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-800 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="text-blue-100">Comprehensive employee management</span>
            </div>
          </div>
        </div>
        
        <p className="text-blue-400 text-sm">
          © {new Date().getFullYear()} DIGIHR. All rights reserved.
        </p>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src="/logo.png" 
              alt="DIGIHR" 
              className="h-16 object-contain"
            />
          </div>

          {step === 1 ? (
            <>
              {/* Step 1: Organization Code */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome back</h2>
                <p className="text-gray-500">Enter your organization code to continue</p>
              </div>

              <form onSubmit={verifyClientCode} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Code
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="clientCode"
                      value={formData.clientCode}
                      onChange={handleChange}
                      placeholder="e.g., TECH01 or 'super'"
                      autoFocus
                      autoComplete="organization"
                      className="w-full pl-11 pr-10 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    />
                    {verifying && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                      </div>
                    )}
                    {clientData && !verifying && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Preview */}
                {clientData && !verifying && (
                  <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    {clientData.logo?.url ? (
                      <img 
                        src={clientData.logo.url} 
                        alt={clientData.name}
                        className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-200 p-1"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-blue-900 flex items-center justify-center">
                        {clientData.isSuperAdmin ? (
                          <Shield className="w-6 h-6 text-white" />
                        ) : (
                          <span className="text-lg font-bold text-white">
                            {clientData.name?.charAt(0)}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {clientData.isSuperAdmin ? 'Super Admin' : clientData.name}
                      </p>
                      <p className="text-sm text-blue-600">Organization verified</p>
                    </div>
                  </div>
                )}

                {(error || formError) && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error || formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={verifying}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <div className="text-center">
                  <Link to="/" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">
                    ← Back to Home
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <>
              {/* Step 2: Login Credentials */}
              <div className="mb-8">
                {/* Organization Header */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200">
                  {clientData?.logo?.url ? (
                    <img 
                      src={clientData.logo.url} 
                      alt={clientData.name}
                      className="w-14 h-14 rounded-lg object-contain bg-white border border-gray-200 p-1"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-blue-900 flex items-center justify-center">
                      {clientData?.isSuperAdmin ? (
                        <Shield className="w-7 h-7 text-white" />
                      ) : (
                        <span className="text-xl font-bold text-white">
                          {clientData?.name?.charAt(0) || 'O'}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 truncate">
                      {clientData?.isSuperAdmin ? 'Super Admin' : clientData?.name}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {clientData?.isSuperAdmin ? 'System Administrator' : 'Sign in to your account'}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="Enter your username"
                      autoFocus
                      autoComplete="username"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                    />
                    <span className="text-sm text-gray-600">Remember me</span>
                  </label>
                  {!clientData?.isSuperAdmin && (
                    <button
                      type="button"
                      onClick={openForgotPassword}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>

                {(error || formError) && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error || formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full py-2 text-sm text-gray-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Change Organization
                </button>
              </form>
            </>
          )}

          {/* Mobile Footer */}
          <p className="lg:hidden text-center text-gray-400 text-sm mt-8">
            © {new Date().getFullYear()} DIGIHR. All rights reserved.
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Reset Password</h3>
                  <p className="text-xs text-gray-500">
                    {fpStep === 1 && 'Enter your registered email'}
                    {fpStep === 2 && 'Verify OTP'}
                    {fpStep === 3 && 'Set new password'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeForgotPassword}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="px-6 pt-4">
              <div className="flex items-center gap-2">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex-1 flex items-center gap-2">
                    <div className={`h-1.5 flex-1 rounded-full transition-colors ${
                      s <= fpStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-6">
              {/* Success Message */}
              {fpSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {fpSuccess}
                </div>
              )}

              {/* Error Message */}
              {fpError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {fpError}
                </div>
              )}

              {/* Step 1: Enter Email */}
              {fpStep === 1 && (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registered Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={fpEmail}
                        onChange={(e) => { setFpEmail(e.target.value); setFpError(''); }}
                        placeholder="Enter your email"
                        autoFocus
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      We'll send a 6-digit OTP to this email address
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={fpLoading}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {fpLoading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Sending OTP...</>
                    ) : (
                      <><Mail className="w-5 h-5" /> Send OTP</>
                    )}
                  </button>
                </form>
              )}

              {/* Step 2: Enter OTP */}
              {fpStep === 2 && (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="text-center mb-2">
                    <p className="text-sm text-gray-600">
                      OTP sent to <span className="font-semibold text-gray-900">{fpMaskedEmail}</span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                      Enter 6-digit OTP
                    </label>
                    <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                      {fpOtp.map((digit, index) => (
                        <input
                          key={index}
                          ref={el => otpRefs.current[index] = el}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          autoFocus={index === 0}
                          className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                        />
                      ))}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={fpLoading || fpOtp.join('').length !== 6}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {fpLoading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Verifying...</>
                    ) : (
                      'Verify OTP'
                    )}
                  </button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={fpResendTimer > 0 || fpLoading}
                      className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 transition-colors flex items-center gap-1 mx-auto"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      {fpResendTimer > 0 ? `Resend OTP in ${fpResendTimer}s` : 'Resend OTP'}
                    </button>
                  </div>
                </form>
              )}

              {/* Step 3: New Password */}
              {fpStep === 3 && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={fpShowPassword ? 'text' : 'password'}
                        value={fpNewPassword}
                        onChange={(e) => { setFpNewPassword(e.target.value); setFpError(''); }}
                        placeholder="Min. 6 characters"
                        autoFocus
                        className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setFpShowPassword(!fpShowPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {fpShowPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={fpShowPassword ? 'text' : 'password'}
                        value={fpConfirmPassword}
                        onChange={(e) => { setFpConfirmPassword(e.target.value); setFpError(''); }}
                        placeholder="Re-enter your password"
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={fpLoading}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {fpLoading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Resetting...</>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
